/** An item's scores, one row per evaluator, each with its reason (and earlier versions' scores). */

import { Markdown } from "@/components/ui/markdown";
import { formatDateTime, formatLatency, formatMoney, formatScore } from "@/lib/format";
import { SlotValues } from "./item-sections";
import type { ExperimentSummary, ItemDetail, ScoreHistoryEntry } from "../types";

/** Earlier scores of this item by the same evaluator, newest version first. */
function ScoreHistory({ name, history }: { name: string; history: ScoreHistoryEntry[] | undefined }) {
  if (!history || history.length < 2) return null;
  const rows = [...history].sort((a, b) => b.version - a.version || b.created_at.localeCompare(a.created_at));
  return (
    <ul
      aria-label={`${name} score history`}
      className="mt-1 flex flex-col gap-0.5 rounded-md bg-well px-2.5 py-1.5 text-[11px]"
    >
      {rows.map((h) => (
        <li key={`${h.scoring_run_id}-${h.version}`} className="flex gap-3">
          <span className="w-8 font-mono text-faint">v{h.version}</span>
          <span className="w-10 font-mono">{formatScore(h.value)}</span>
          <span className="text-muted">{formatDateTime(h.created_at)}</span>
        </li>
      ))}
    </ul>
  );
}

export function Scores({ item, experiment }: { item: ItemDetail; experiment: ExperimentSummary }) {
  if (item.status === "agent_error")
    return <p className="text-[13px] text-muted">Skipped: the agent returned no answer.</p>;
  return (
    <div className="flex flex-col">
      {experiment.evaluators.map((ev) => {
        const s = item.scores[ev.key];
        const meta =
          s &&
          [
            s.model,
            s.cost_usd !== null && formatMoney(s.cost_usd),
            s.latency_ms !== null && formatLatency(s.latency_ms),
          ]
            .filter(Boolean)
            .join(" · ");
        return (
          <div
            key={ev.key}
            className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 border-t border-line-soft py-3"
          >
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-[13px] font-semibold">
                {ev.name}
                {s?.version !== undefined && (
                  <span className="ml-1.5 font-mono text-[11px] font-normal text-faint">v{s.version}</span>
                )}
              </span>
              <div className="text-[13px] leading-normal text-muted">
                <Markdown>
                  {!s
                    ? "Not scored yet."
                    : s.status === "not_applicable"
                      ? "Not applicable to this item."
                      : (s.reason ?? "No reason given.")}
                </Markdown>
              </div>
              {meta && <span className="src">{meta}</span>}
              <SlotValues slots={s?.slots} evaluator={ev.name} />
              <ScoreHistory name={ev.name} history={s?.history} />
            </div>
            <span
              className={`score self-start text-sm ${s?.passed === false ? "score-fail" : ""} ${s?.status === "error" ? "score-err" : ""}`}
            >
              {!s || s.status === "not_applicable"
                ? "n/a"
                : s.status === "error"
                  ? "err"
                  : formatScore(s.value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
